













































import { Component, Vue, Watch } from 'vue-property-decorator';
import user from '@/store/modules/user';
import cart from '@/store/modules/cart';
import ui from '@/store/modules/ui';
import checkout from '@/store/modules/checkout';
import storelocator from '@/store/modules/storelocator';
import { QuoteAddress, Country, ShippingMethod } from '@/store/models';
import Summary from '@/components/Checkout/CartSummary.vue';
import CourierMethod from '@/components/Checkout/ShippingStep/CourierMethod.vue';
import StorePickupMethod from '@/components/Checkout/ShippingStep/StorePickupMethod.vue';
import PickupPointMethod from '@/components/Checkout/ShippingStep/PickupPointMethod.vue';
import RuPickupMethod from '@/components/Checkout/ShippingStep/RuPickupMethod.vue';
import SelectedShippingInfo from '@/components/Checkout/ShippingStep/SelectedShippingInfo.vue';
import store from '@/store';
import { i18n } from '@/i18n';
import { encode as encodeShipment, decode as decodeShipment, isRuPickupMethod, getSimilarMethod } from '@/commons/shipmentMethodHelpers';
import gtmhelpers from '@/commons/gtmhelpers';
import {scrollTo} from "@/commons/scrollto";
import pickuppoint from '@/store/modules/pickuppoint';

@Component({
  components: {
    Summary,
    CourierMethod,
    StorePickupMethod,
    RuPickupMethod,
    PickupPointMethod,
    SelectedShippingInfo,
  },
  provide() {
    return {
      $validator: this.$validator,
    };
  },
})
export default class ShippingStep extends Vue {
  private changeAddressMode: string = ''; // save | update
  private loading = false;

  get selected(): string {
    return checkout.shipmethodSelection;
  }

  set selected(val: string) {
    checkout.setShipmethodSelection(val);
  }

  get isShippingMethodsLoading(): boolean {
    return cart.loadingFlags.shipMethods;
  }

  get isLogged(): boolean {
    return user.isLoggedin;
  }

  get hasPlaceError(): boolean {
    return checkout.hasPlaceErrorByCode('shipping_address') || checkout.hasPlaceErrorByCode('shipping_selection');
  }

  get hasAddresses(): boolean {
    return user.customer.addresses.length > 0;
  }

  get address(): QuoteAddress {
    return checkout.shippingAddress;
  }

  get prefix(): string {
    const customAttr = checkout.shippingAddress.custom_attributes;
    const finded = customAttr.find((element: any) => element.attribute_code === 'tdy_country_code');
    return finded.value;
  }

  get countries(): Country[] {
    return store.state.countryData.countries;
  }

  get methods(): ShippingMethod[] {
    const priority: any = {
      default: 90,
      storepickup: 100,
    };
    const methods = checkout.shippingMethods;
    methods.sort((a: any, b: any) => {
      const pa = priority[a.carrier_code] || priority.default;
      const pb = priority[b.carrier_code] || priority.default;
      return pa < pb ? -1 : (pb < pa ? 1 : 0);
    });
    return methods;
  }

  get isItStorePickup(): boolean {
    return this.selected === store.state.options.carrier.pickup_method;
  }

  get fulfilled(): boolean {
    return checkout.stepByName('shipping').fulfilled;
  }

  get unlocked(): boolean {
    return checkout.stepByName('shipping').unlocked;
  }

  get saveText() {
    return store.state.options.carrier.multicarrier ? 'Further' : 'Confirm';
  }

  @Watch('selected')
  private onChangeSelected(val: any, oldVal: any) {
    const methodsCheck = [store.state.options.carrier.pickup_method, 'pickuppoints_pickuppoints'];
    if (val && methodsCheck.indexOf(val) === -1) {
      this.save(val, this.address);
    }
  }

  @Watch('unlocked')
  private onChangeUnlocked(val: any, oldVal: any) {
    const shippingMethodPreselect = store.state.options.carrier.preselectShipping;
    if (shippingMethodPreselect && !this.selected) {
      const preselectedCarrier = this.methods.find((method) => method.carrier_code === shippingMethodPreselect);
      // default shipping method preselect
      if (!this.selected && preselectedCarrier) {
        this.selected = encodeShipment(preselectedCarrier);
      } else if (!this.selected && checkout.shippingMethods && checkout.shippingMethods.length === 1) {
        this.selected = encodeShipment(checkout.shippingMethods[0]);
      }
    }
  }

  private editStep() {
    this.selected = '';
    user.setEditMode({mode: '', selectedId: undefined});
    checkout.stepByName('shipping').fulfilled = false;
  }

  private editShipping() {
    if (this.isItStorePickup) {
      ui.openModal('storepickupSelection');
    } else if(this.selected === 'pickuppoints_pickuppoints') {
      ui.openModal('pickuppointModal');
      pickuppoint.openMapClickBtn();
    } else {
      user.setEditMode({mode: '', selectedId: undefined});
      checkout.stepByName('shipping').fulfilled = false;
      checkout.flagEditShippingAddress(true);
    }
  }

  private isActive(method: ShippingMethod): boolean {
    return this.getKey(method) === this.selected;
  }

  private getKey(method: ShippingMethod) {
    return encodeShipment(method);
  }

  private getShippingMethodComponentName(method: ShippingMethod): any {
    if (encodeShipment(method) === store.state.options.carrier.pickup_method) {
      return { 
        name: 'StorePickupMethod',
        on: { storePickupSelected: this.storeSelectedHandler }
      };
    } else if (method.carrier_code === 'owsh1') {
      return { 
        name: 'CourierMethod',
        on: { courierAddressConfirmed: this.courierAddressConfirmedHandler }
      };
    } else if (method.carrier_code === 'matrixrate') {
      return isRuPickupMethod(method) ? 
        { name: 'RuPickupMethod', on: {} } : 
        {
          name: 'CourierMethod',
          on: { courierAddressConfirmed: this.courierAddressConfirmedHandler }
        };
    } else if (method.carrier_code === 'pickuppoints') {
      return {
        name: 'PickupPointMethod',
        on: { pickupPointSelected: this.pickupPointSelectedHandler }
      };
    }
    return {};
  }

  private courierAddressConfirmedHandler(address: any){
    this.save(this.selected, address);
  }

  private storeSelectedHandler() {
    this.save(this.selected, this.address);
  }

  private pickupPointSelectedHandler(){
    this.save(this.selected, this.address);
  }

  private async save(selectedMethod: string, address: QuoteAddress) {
    if (selectedMethod.toString() === '') {
      this.$toast.err(i18n.t('Verify shipping method selection').toString());
      return;
    }
    const isDefaultStorePickup = selectedMethod === store.state.options.carrier.pickup_method;
    if (isDefaultStorePickup && storelocator.selectedStoreId.toString() === '') {
      this.$toast.err(i18n.t('Choose a store for the pick-up in order to proceed').toString());
      return;
    }
    const valid = await this.$validator.validateAll();
    if (!valid) {
      return;
    }

    user.copySelectedAddressToQuoteAddress('shipping');

    const isMulticarrier = store.state.options.carrier.multicarrier;

    if (user.editAddressMode !== '') {
      try {
        await user.saveUserAddress({
          address: user.editAddress,
          addressType: 'shipping',
          mode: user.editAddressMode,
          editingAddressId: checkout.selectedAddressId.shipping,
        });
      } catch (e) {
        const ex: any = e;
        this.$toast.err(ex.message);
        return;
      }
    }

    try {
      const shippingMethodObj = checkout.findShippingMethod(selectedMethod);
      if (isMulticarrier) {
        const isRuStorePickup = !cart.cart.is_virtual && (isRuPickupMethod(shippingMethodObj) || isDefaultStorePickup);
        if (isRuStorePickup) {
          checkout.copyBillingAddressToShippingAddress();
        }
        const seletedShipMethod = checkout.selectedShippingMethod;
        const oldMethods = JSON.stringify(checkout.shippingMethods.map((m) => m.method_code));
        await checkout.estimateShippingSimple(address);
        const newMethods = JSON.stringify(checkout.shippingMethods.map((m) => m.method_code));
        if (oldMethods !== newMethods) {
          const similarMethod = getSimilarMethod(seletedShipMethod, checkout.shippingMethods);

          if (similarMethod) {
            checkout.setSelectedShipment(encodeShipment(similarMethod));
            selectedMethod = checkout.selectedShipment;
          } else {
            checkout.setSelectedShipment('');
            return;
          }
        }
      }
      checkout.setShippingInfo({
        address,
        shippingmethod: decodeShipment(selectedMethod),
      }).then(() => {
        checkout.setSelectedShipment(selectedMethod);
      });
      checkout.flagEditShippingAddress(false);

      checkout.resetPlaceErrors('shipping_selection');
      checkout.resetPlaceErrors('pickup_selection');
      checkout.resetPlaceErrors('shipping_address');

        scrollTo('.shipping-step', 30);

      checkout.goingToStep({fromStep: 'shipping', toStep: 'payment'});
      gtmhelpers.trackShippingInfo(
          gtmhelpers.convertProductsData(cart.cart.items),
          cart.totals.grand_total,
          cart.totals.quote_currency_code,
          checkout.coupon.coupon,
          selectedMethod);
    } catch (e) {
      console.log(e);
    }
  }
}
